import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  SwapVert as SortIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

const DetailHistoryPopup = ({ isOpen, onClose, title, columns, data }) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: data || [],
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
        {/* Backdrop - Subtle Darkening (No Blur) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/70 pointer-events-auto"
        />

        {/* Modal Content - Fixed Size and Solid BG */}
        <motion.div
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.98, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="relative w-[1100px] h-[700px] bg-[#0d1221] rounded-[32px] shadow-[0_40px_120px_rgba(0,0,0,1)] border border-white/10 flex flex-col overflow-hidden pointer-events-auto"
        >
          {/* Enhanced Premium Glows - Less Blur/Spread */}
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-indigo-500/[0.04] blur-[80px] pointer-events-none rounded-full" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/[0.04] blur-[80px] pointer-events-none rounded-full" />

          {/* Header Section - Reduced Padding */}
          <div className="px-8 py-6 flex justify-between items-center z-10 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-1 h-10 bg-indigo-500 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.5)]" />
              <div>
                <h2 className="text-[26px] font-black text-white tracking-tighter leading-tight">
                  {title}
                </h2>
                <div className="flex items-center gap-3 mt-0.5">
                  <p className="text-slate-500 text-[11px] font-bold tracking-tight">
                    Total <span className="text-slate-100">{table.getFilteredRowModel().rows.length}</span> records detected
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Search Bar - Slimmer */}
              <div className="relative group">
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors"
                  sx={{ fontSize: 16 }}
                />
                <input
                  type="text"
                  value={globalFilter ?? ''}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="상세 내역 검색..."
                  className="bg-white/5 border border-white/5 rounded-xl pl-9 pr-4 py-2 text-[13px] text-white w-72 focus:outline-none focus:bg-white/[0.08] focus:border-indigo-500/30 transition-all placeholder:text-slate-600 font-bold"
                />
              </div>

              <button
                onClick={onClose}
                className="p-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-white hover:bg-red-500/20 hover:border-red-500/20 transition-all active:scale-90 border border-white/5"
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
          </div>

          {/* Data Table Section */}
          <div className="flex-1 overflow-auto px-8 pb-6 z-10
            [&::-webkit-scrollbar]:w-1 
            [&::-webkit-scrollbar-thumb]:bg-white/10 
            [&::-webkit-scrollbar-thumb]:rounded-full 
            [&::-webkit-scrollbar-track]:bg-transparent"
          >
            <table className="w-full border-separate border-spacing-y-2">
              <thead className="sticky top-0 z-20">
                <tr className="bg-[#0a0f1e]/80 backdrop-blur-xl">
                  {table.getHeaderGroups().map(headerGroup => (
                    headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                        className={`px-5 py-4 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-left transition-all ${header.column.getCanSort() ? 'cursor-pointer hover:text-indigo-400 select-none group' : ''
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                              {{
                                asc: <ArrowUpIcon sx={{ fontSize: 13 }} className="text-indigo-400" />,
                                desc: <ArrowDownIcon sx={{ fontSize: 13 }} className="text-indigo-400" />,
                              }[header.column.getIsSorted()] ?? <SortIcon sx={{ fontSize: 13 }} />}
                            </div>
                          )}
                        </div>
                      </th>
                    ))
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    className="group"
                  >
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className="px-5 py-3.5 bg-white/[0.015] border-y border-white/[0.03] first:border-l first:rounded-l-xl last:border-r last:rounded-r-xl text-[12px] text-slate-400 font-bold group-hover:bg-indigo-500/5 group-hover:text-slate-100 group-hover:border-indigo-500/10 transition-all duration-200"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {table.getRowModel().rows.length === 0 && (
              <div className="h-80 flex flex-col items-center justify-center text-slate-500 gap-5 bg-white/[0.01] rounded-3xl border border-dashed border-white/5 mt-4">
                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center opacity-40">
                  <SearchIcon sx={{ fontSize: 32 }} />
                </div>
                <div className="text-center">
                  <p className="font-black tracking-widest uppercase text-xs opacity-50">No results found</p>
                  <p className="text-[11px] text-slate-600 mt-1 uppercase tracking-tight">Try adjusting your search filters</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Decoration - Slimmer */}
          <div className="px-8 py-3.5 border-t border-white/5 bg-black/20 z-10 font-bold">
            <div className="flex justify-between items-center text-[9px] font-black text-slate-600 uppercase tracking-[0.2em]">
              <div className="flex items-center gap-4">
                <span>© 2026 INSIGHTWIN PRECISION</span>
                <span className="w-1 h-1 bg-slate-800 rounded-full" />
                <span className="text-indigo-500/30">History Log Viewer v2.1</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[8px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 uppercase">Security Level 4</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DetailHistoryPopup;
